<template>
  <dialog ref="dialogRef" class="bug-report-dialog" @close="handleClose" @click="handleBackdropClick">
    <div class="bug-report-card" :class="{ mobile: isMobile }">
      <div class="bug-report-title">
        {{ localize('dialog.bug_report.title') }}
      </div>

      <div v-if="errorMessage" class="bug-report-error">
        <gam-button-message v-bind="errorMessage" />
      </div>

      <div class="bug-report-textarea">
        <gam-textarea
          ref="textareaRef"
          v-model="message"
          :placeholder="'dialog.bug_report.placeholder'"
          :value="message"
        />
      </div>

      <div class="bug-report-actions">
        <button class="bug-report-submit" :disabled="!message?.trim() || isLoading" @click="handleSubmit">
          {{ localize('dialog.bug_report.button') }}
        </button>
      </div>

      <div class="bug-report-close" @click="handleClose">
        <GamIcon :name="GamIconName.X_CLOSE" size="24px" is-current />
      </div>
    </div>
  </dialog>
</template>

<script setup lang="ts">
import { gambits, localize } from '@/core/gambits';
import { isAndroid, isIOS, isMobile, isPWA } from '@/core/helpers/ui.helper';
import { platform } from '@/core/platform';
import GamButtonMessage from '@/views/components/GamButtonMessage.vue';
import GamIcon from '@/views/components/GamIcon.vue';
import GamTextarea from '@/views/components/GamTextarea.vue';
import { GamButtonMessageVariant } from '@/views/composables/constants/components/gamButton.constants';
import { GamIconName } from '@/views/composables/constants/components/gamIcon.constants';
import { GamComponentsEmits } from '@/views/composables/constants/main/emit.constants';
import type { GamButtonMessageType } from '@/views/composables/models/components/GamButton';
import { onBeforeUnmount, onMounted, ref } from 'vue';
import { useRoute } from 'vue-router';
import { useToast } from 'vue-toastification';

const emits = defineEmits([GamComponentsEmits.CLOSE]);

const dialogRef = ref<HTMLDialogElement>();
const textareaRef = ref<InstanceType<typeof GamTextarea>>();
const message = ref<string | null>(null);
const isLoading = ref(false);
const errorMessage = ref<GamButtonMessageType | null>(null);
const route = useRoute();

onMounted(() => {
  dialogRef.value?.showModal();
});

onBeforeUnmount(() => {
  if (dialogRef.value?.open) dialogRef.value.close();
});

function collectMetadata(): Record<string, unknown> {
  return {
    userAgent: navigator.userAgent,
    screen: `${screen.width}x${screen.height}`,
    viewport: `${window.innerWidth}x${window.innerHeight}`,
    dpr: window.devicePixelRatio,
    isNative: platform.isNative,
    isPWA: isPWA.value,
    isIOS: isIOS.value,
    isAndroid: isAndroid.value,
    isMobile: isMobile.value,
    route: route.fullPath,
    timestamp: new Date().toISOString(),
  };
}

const handleClose = (e?: Event): void => {
  e?.preventDefault();
  emits(GamComponentsEmits.CLOSE);
};

const handleBackdropClick = (e: MouseEvent): void => {
  if (e.target === dialogRef.value) {
    emits(GamComponentsEmits.CLOSE);
  }
};

const handleSubmit = async () => {
  if (!message.value?.trim() || isLoading.value) return;

  isLoading.value = true;

  errorMessage.value = null;

  try {
    await gambits.bugReportApi.create({
      message: message.value.trim(),
      metadata: collectMetadata(),
    });
    useToast().success(localize('dialog.bug_report.sent'));
    emits(GamComponentsEmits.CLOSE);
  } catch (error: any) {
    const msg =
      error?.response?.status === 429
        ? localize('dialog.bug_report.rate_limit')
        : 'Something went wrong. Please try again.';

    errorMessage.value = {
      label: msg,
      variant: GamButtonMessageVariant.ERROR,
    };

    setTimeout(() => {
      errorMessage.value = null;
    }, 5000);
  } finally {
    isLoading.value = false;
  }
};
</script>

<style lang="scss" scoped>
@use '@/ui/css/mixins' as *;
.bug-report-dialog {
  width: 100%;
  height: 100%;
  border: 0;
  padding: 0;
  margin: 0;
  max-width: 100%;
  max-height: 100%;
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  outline: none;
  overflow: hidden;

  &::backdrop {
    background: rgba(23, 23, 23, 0.75);
  }
}

.bug-report-card {
  position: relative;
  display: flex;
  flex-direction: column;
  padding: var(--spacing-xxl) var(--spacing-xl);
  padding-bottom: 6.5rem;
  background: var(--color-dark-700);
  border: 1px solid var(--color-white-10);
  border-radius: var(--radius-large);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.5);
  max-width: 335px;
  width: 100%;
  gap: var(--spacing-md);

  &.mobile {
    max-width: 88vw;
  }
}

.bug-report-title {
  font-size: 20px;
  font-weight: 700;
  color: var(--color-white);
  text-align: center;
  padding-bottom: var(--spacing-md);
}

.bug-report-error {
  margin-bottom: var(--spacing-sm);
}

.bug-report-textarea {
  width: 100%;
}

.bug-report-actions {
  margin-top: var(--spacing-md);
  width: 100%;
}

.bug-report-submit {
  width: 100%;
  padding: 0.875rem 1.5rem;
  border: none;
  border-radius: 2rem;
  background: var(--color-linear-gradient);
  color: #fff;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  touch-action: manipulation;
  transition: opacity 200ms ease;

  @include hover {
    opacity: 0.9;
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
}

.bug-report-close {
  position: absolute;
  bottom: var(--safe-bottom-offset);
  left: 50%;
  transform: translateX(-50%);
  width: 3.5rem;
  height: 3.5rem;
  border-radius: 3.5rem;
  border: 1px solid var(--color-white-10);
  backdrop-filter: blur(8px);
  background-color: rgba(0, 0, 0, 0.2);
  color: var(--color-white-70);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  touch-action: manipulation;
  transition: all 200ms linear;

  @include hover {
    color: var(--color-white);
    border-color: var(--color-white-20);
  }

  @include press($transform: translateX(-50%));
}
</style>
